import React, { useState } from 'react';
import { Alert, Button, Card, DatePicker, Empty, Form, Modal, Progress, Spin, Typography } from 'antd';
import {
  InfoCircleOutlined,
  LinkOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { OrderTransportsDto } from '../../../dto/transport';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { TransportOrderRealizationCard } from '../../transports/components/TransportOrderRealizationCard';
import TransportDeliveryQuantityFields, {
  buildTransportDeliveryFormItems,
  type TransportDeliveryFormItem,
} from '../../transports/components/TransportDeliveryQuantityFields';
import OrderTransportLinkModal from './OrderTransportLinkModal';
import { withTransportReturnContext } from '../../transports/transportReturnContext';
import TransportStatusPicker from '../../../components/TransportStatusPicker';
import { transportDatePickerProps } from '../../../components/transportFormUtils';
import dayjs, { type Dayjs } from 'dayjs';
import './OrderTransportsSection.css';
import { getTransportLabel } from '../../../utils/transportLabel';

interface OrderTransportsSectionProps {
  orderId: string;
  orderTransports: OrderTransportsDto;
  loading: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canManageStatus?: boolean;
  canViewCosts?: boolean;
  onDelete: (transportId: string) => Promise<void> | void;
  onStatusChange?: (transportId: string, payload: {
    status: string;
    actualDelivery?: string | null;
    deliveryItems?: TransportDeliveryFormItem[];
  }) => Promise<boolean>;
}

/**
 * Transport tab on the order detail page.
 *
 * A transport is an independent N:N entity that an order is LINKED to (not
 * owned by the order) — the UI copy uses "Powiąż transport" / "Link transport"
 * to match. The per-transport card here shows only the per-order slice
 * (carrier, dates, THIS order's cargo allocation) and deep-links to the
 * Transports tab for full edits. Curating the full form from inside the order
 * was the original UX pain.
 */
const OrderTransportsSection: React.FC<OrderTransportsSectionProps> = ({
  orderId,
  orderTransports,
  loading,
  canEdit,
  canDelete,
  canManageStatus = false,
  canViewCosts = false,
  onDelete,
  onStatusChange,
}) => {
  const { t, i18n } = useTranslation(['transport', 'common']);
  const navigate = useNavigate();
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [statusTransportId, setStatusTransportId] = useState<string | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusForm] = Form.useForm();
  const editedStatus = Form.useWatch('status', statusForm);

  if (loading) {
    return (
      <Card>
        <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />
      </Card>
    );
  }

  const handleOpenTransport = (transportId: string) => navigate(
    withTransportReturnContext(`/app/transports/${encodeURIComponent(transportId)}`, orderId),
  );

  const linkButton = canEdit ? (
    <Button type="primary" icon={<LinkOutlined />} onClick={() => setLinkModalOpen(true)}>
      {t('transport:orderTransports.linkTransport')}
    </Button>
  ) : null;

  const linkModal = (
    <OrderTransportLinkModal
      open={linkModalOpen}
      orderId={orderId}
      linkedTransportIds={orderTransports.transports.map((transport) => transport.id)}
      onClose={() => setLinkModalOpen(false)}
    />
  );

  if (orderTransports.transports.length === 0) {
    return (
      <>
        <Card>
          <Empty
            image={<TruckOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
            description={t('transport:orderTransports.empty')}
          >
            {linkButton}
          </Empty>
        </Card>
        {linkModal}
      </>
    );
  }

  const quantity = (value: number) => formatLocalizedQuantity(value, i18n.language);
  const activeTransportQuantity = (orderProductId: string) => {
    const realization = orderTransports.productRealization.find((item) => item.orderProductId === orderProductId);
    if (!realization) return '—';
    const activePhysicalQuantity = Math.max(
      realization.allocatedQuantity - Number(realization.deliveredQuantity || 0),
      0,
    );
    const physical = `${quantity(activePhysicalQuantity)} ${formatUnit(realization.unit, i18n.language)}`;
    const activeCommercialQuantity = realization.commercialUnit
      && realization.orderedCommercialQuantity != null
      && realization.orderedQuantity > 0
      ? realization.orderedCommercialQuantity * activePhysicalQuantity / realization.orderedQuantity
      : null;
    return realization.commercialUnit && activeCommercialQuantity != null
      ? `${physical} / ${quantity(activeCommercialQuantity)} ${formatUnit(realization.commercialUnit, i18n.language)}`
      : physical;
  };
  const hasProgress = orderTransports.productRealization.length > 0;
  const selectedTransport = orderTransports.transports.find((transport) => transport.id === statusTransportId);
  const openStatusModal = (transportId: string) => {
    const transport = orderTransports.transports.find((item) => item.id === transportId);
    if (!transport) return;
    statusForm.setFieldsValue({
      status: transport.status,
      actualDelivery: transport.actualDelivery ? dayjs(transport.actualDelivery) : null,
      deliveryItems: buildTransportDeliveryFormItems(transport.cargoItems),
    });
    setStatusTransportId(transportId);
  };
  const closeStatusModal = () => {
    if (statusSaving) return;
    setStatusTransportId(null);
    statusForm.resetFields();
  };
  const saveStatus = async (values: {
    status: string;
    actualDelivery?: Dayjs | null;
    deliveryItems?: TransportDeliveryFormItem[];
  }) => {
    if (!statusTransportId || !onStatusChange) return;
    setStatusSaving(true);
    try {
      const saved = await onStatusChange(statusTransportId, {
        status: values.status,
        ...(values.status === 'DELIVERED'
          ? {
              actualDelivery: values.actualDelivery?.format('YYYY-MM-DD') || null,
              deliveryItems: values.deliveryItems || [],
            }
          : { actualDelivery: null }),
      });
      if (saved) {
        setStatusTransportId(null);
        statusForm.resetFields();
      }
    } finally {
      setStatusSaving(false);
    }
  };

  const progressColor = (percent: number) => percent >= 100 ? '#07875d' : percent > 0 ? '#d48806' : '#cf3f3f';
  const progressState = (percent: number) => percent >= 100 ? 'is-green' : percent > 0 ? 'is-yellow' : 'is-red';

  return (
    <section className="order-transports-section">
      <div className="order-transports-section__toolbar">
        <div><Typography.Title level={4}>{t('transport:orderTransports.sectionTitle')}</Typography.Title><Typography.Text type="secondary">{t('transport:orderTransports.sectionDescription')}</Typography.Text></div>
        {linkButton}
      </div>

      {hasProgress && (
        <div className="order-transport-readiness">
          <header><div><Typography.Title level={5}>{t('transport:orderTransports.productProgressTitle')}</Typography.Title><Typography.Text type="secondary">{t('transport:orderTransports.productProgressDescription')}</Typography.Text></div></header>
          <Alert
            type="info"
            showIcon
            message={t('transport:orderTransports.allocationHint')}
            className="order-transport-readiness__hint"
          />
          <div className="order-transport-readiness-list">
            {orderTransports.productRealization.map((row) => {
                const deliveredQuantity = Number(row.deliveredQuantity || 0);
                const deliveryPercent = Number(row.deliveryPercent || 0);
                const unit = formatUnit(row.unit, i18n.language);
                return <div className={`order-transport-readiness-row ${progressState(deliveryPercent)}`} key={row.orderProductId}>
                  <div className="order-transport-readiness-row__product"><strong>{row.name}</strong><span>{t('transport:orderTransports.orderedColumn')}: {quantity(row.orderedQuantity)} {unit}</span></div>
                  <div className="order-transport-readiness-row__progress"><div><span>{t('transport:orderTransports.deliveredOfOrdered', { delivered: quantity(deliveredQuantity), ordered: quantity(row.orderedQuantity), unit })}</span><strong style={{ color: progressColor(deliveryPercent) }}>{quantity(deliveryPercent)}%</strong></div><Progress percent={Math.min(100, deliveryPercent)} showInfo={false} strokeColor={progressColor(deliveryPercent)} trailColor="#e8edf3" /></div>
                  <div className="order-transport-readiness-row__ready"><span>{t('transport:orderTransports.inTransportColumn')}</span><strong>{activeTransportQuantity(row.orderProductId)}</strong><span>{t('transport:orderTransports.remainingColumn')}</span><strong>{quantity(row.remainingQuantity)} {unit}</strong></div>
                </div>;
              })}
          </div>
        </div>
      )}

      <div className="order-transports-section__list">{orderTransports.transports.map((transport) => (
          <TransportOrderRealizationCard
            key={transport.id}
            orderId={orderId}
            transport={transport}
            showCosts={canViewCosts}
            onUnlink={canDelete ? () => onDelete(transport.id) : undefined}
            onEditStatus={canManageStatus && onStatusChange ? () => openStatusModal(transport.id) : undefined}
            onOpen={() => handleOpenTransport(transport.id)}
          />
        ))}</div>
      {linkModal}
      <Modal className="order-transports-section__status-modal" width={620} title={t('transport:orderTransports.changeStatusTitle', { number: getTransportLabel(selectedTransport) })} open={Boolean(statusTransportId)} onCancel={closeStatusModal} footer={null} destroyOnHidden>
        <Form form={statusForm} layout="vertical" onFinish={saveStatus}>
          <Form.Item name="status" label={t('transport:fields.status')} rules={[{ required: true, message: t('transport:pleaseSelectStatus') }]}><TransportStatusPicker /></Form.Item>
          {editedStatus === 'DELIVERED' && <><Form.Item name="actualDelivery" label={t('transport:fields.actualDelivery')} rules={[{ required: true, message: t('transport:validation.actualDeliveryRequired') }]}><DatePicker {...transportDatePickerProps} style={{ width: '100%' }} /></Form.Item><div className="order-transports-section__status-hint"><InfoCircleOutlined /><span>{t('transport:orderTransports.deliveredStatusHint')}</span></div>{selectedTransport ? <TransportDeliveryQuantityFields cargoItems={selectedTransport.cargoItems} routeOrders={selectedTransport.routeOrders} /> : null}</>}
          <div className="order-transports-section__modal-actions"><Button onClick={closeStatusModal}>{t('common:cancel')}</Button><Button type="primary" htmlType="submit" loading={statusSaving}>{t('transport:updateStatus')}</Button></div>
        </Form>
      </Modal>
    </section>
  );
};

export default OrderTransportsSection;
